import { Guid } from "guid-typescript";
import { isEqual } from 'lodash'

import BaseButtonBuilder from '../Buttons/index'
import { ButtonTypes } from '../Buttons/types'
import { BaseButton } from '../Buttons/types'
import RowOfButtons from './RowOfButtons'
import { AvailableButtonsTypes, ButtonBuildersGroups } from './types'

export default class ButtonsManager {
  builders: Record<string, BaseButtonBuilder> = {}
  buildersList: AvailableButtonsTypes = []

  private _rows: Array<RowOfButtons>

  selectedButton: BaseButton | null = null

  currentBuilder: null | BaseButtonBuilder = null

  get rows(): Array<RowOfButtons> {
    return this._rows
  }

  set rows(rows) {
    this._rows = rows

    if (this.onButtonsUpdate) {
      this.onButtonsUpdate()
    }
  }

  constructor(buttons: Array<Array<BaseButton>>, builders: AvailableButtonsTypes) {
    this.updateBuildersList(buttons, builders)

    this._rows = buttons.map((row) => new RowOfButtons(row))
  }

  updateBuildersList(buttons: Array<Array<BaseButton>>, builders: AvailableButtonsTypes) {
    this.buildersList = []

    builders.forEach((b) => {
      this.buildersList.push(b)

      if (b instanceof ButtonBuildersGroups) {
        b.items.forEach((builder) => this.builders[builder.type] = builder)
      } else {
        this.builders[b.type] = b
      }

    })
  }

  updateButton(): void {
    if (this.currentBuilder && this.selectedButton) {
      const newButton = this.currentBuilder.create()
      newButton.guid = this.selectedButton.guid

      const row = this.rows.find((r) => this.selectedButton && r.getButton(this.selectedButton.guid))

      if (row) {
        row.buttons.splice(row.buttons.indexOf(this.selectedButton), 1, newButton)
        this.updateButtons()
      }
    }
  }

  createButton(): void {
    if (this.currentBuilder) {
      this.onNewString(this.currentBuilder.create())
    }
  }

  removeButton(): void {
    const row = this.rows.find((r) => this.selectedButton && r.getButton(this.selectedButton.guid))

    if (this.selectedButton && row) {
      row.removeButton(this.selectedButton)
      this.unselectButton()

      if (!row.buttons.length) {
        this.removeRow(row.guid)
      }

      this.updateButtons()
    }
  }

  setCurrentButton(buttonName: ButtonTypes | string): void {
    this.currentBuilder = this.builders[buttonName]
    this.currentBuilder.refreshData()
  }

  resetCurrentButton(): void {
    if (this.currentBuilder) {
      this.currentBuilder.refreshData()
    }

    this.currentBuilder = null
  }

  selectButton(buttonGuid: string, rowGuid: string): void {
    const row = this._rows.find((r) => r.guid === rowGuid)

    if (row) {
      const button = row.getButton(buttonGuid)

      if (button) {
        if (isEqual(this.selectedButton, button)) {
          this.unselectButton()
        } else {
          this.selectedButton = button

          if (!this.selectedButton?.guid) {
            this.selectedButton.guid = Guid.create().toString()
          }

          this.setCurrentButton(button.type)

          if (this.currentBuilder) {
            this.currentBuilder.data = this.currentBuilder.getData(this.selectedButton)
          }
        }
      }
    }
  }

  unselectButton(): void {
    this.selectedButton = null
    this.resetCurrentButton()
  }

  onNewString(button: BaseButton): void {
    this.rows = [
      ...this.rows,
      new RowOfButtons([ button ])
    ]
  }

  getRow(guid: string): RowOfButtons | undefined {
    return this.rows.find((r) => r.guid === guid)
  }

  removeRow(guid: string): void {
    const row = this._rows.find((r) => r.guid === guid)

    if (row) {
      this.rows = this.rows.filter((r) => r.guid !== row.guid)
    }
  }

  addOnButtonsUpdateHandler(callback: (buttons: Array<Array<BaseButton>>) => void): void {
    this.onButtonsUpdate = () => callback(this.rows.map((r) => r.getModel()))
  }
  /** Обработчик события, который срабатывает после какого-либо изменения кнопок */
  onButtonsUpdate: null | (() => void) = null

  updateButtons(): void {
    if (this.onButtonsUpdate) {
      this.onButtonsUpdate()
    }
  }
}
